import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {concat, from, Observable, of, partition, throwError, TimeoutError, timer} from 'rxjs';
import {catchError, concatMap, ignoreElements, map, pluck, shareReplay, switchMap, timeout} from 'rxjs/operators';

import {environment} from '@environment/environment';
import {AbstractPlatform} from '@app/platform/platform';

export interface RegisterStatus {
    type: 'COMPLETE' | 'PROGRESS';
    value: string;
}

@Injectable()
export class RegisterService {
    constructor(
        private readonly http: HttpClient,
        private readonly platform: AbstractPlatform,
    ) {
    }

    checkRegister(setupCode: string): Observable<any> {
        return this.registerInternal(setupCode, false)
            .pipe(map((responseText: string) => JSON.parse(responseText)));
    }

    register(setupCode: string): Observable<string> {
        return this.registerInternal(setupCode, true);
    }

    pollRegister(): Observable<RegisterStatus> {
        return from(this.platform.getSerialNumber()).pipe(
            switchMap(serial => this.http.post(
                `${environment.serverAddress}/api/smartscreen/register-serial`,
                {serial},
                {responseType: 'text'},
            )),
            // Insert success message
            concatMap(response => from([
                {type: 'PROGRESS', value: 'Erfolglreich'} as RegisterStatus,
                {type: 'COMPLETE', value: response} as RegisterStatus,
            ])),
            timeout(10_000),
            // When the request errors (403 until we are registered) we retry after 5s
            catchError(error => error instanceof HttpErrorResponse || error instanceof TimeoutError
                ? concat(
                    of({type: 'PROGRESS', value: registerErrorToString(error)} as RegisterStatus),
                    timer(5_000).pipe(ignoreElements()),
                    this.pollRegister(),
                )
                : throwError(error)),
        );
    }

    pollRegisterPartitioned(): { secret$: Observable<string>, progress$: Observable<string> } {
        const [secret$, progress$] = partition(
            this.pollRegister().pipe(shareReplay({refCount: true})),
            status => status.type === 'COMPLETE',
        ).map(status$ => status$.pipe(pluck('value')));

        return {secret$, progress$};
    }

    private registerInternal(setupCode: string, confirm: boolean): Observable<string> {
        const url = environment.serverAddress + '/api/smartscreen/register';
        return from(this.platform.getSerialNumber()).pipe(
            switchMap(serial =>
                this.http.post(url, {
                    setupCode,
                    confirm,
                    serial,
                }, {
                    responseType: 'text',
                }),
            ),
        );
    }
}

function registerErrorToString(error: any): string {
    if (error instanceof TimeoutError) {
        return 'Keine Antwort!';
    }

    if (error instanceof HttpErrorResponse) {
        if (error.status === 403) {
            if (error.error === 'unknown_serial') {
                return 'Unbekannte Seriennummer!';
            }
            if (error.error === 'already_registered') {
                return 'Seriennummer bereits zugeordnet!';
            }
        }
        if (error.status / 100 === 5) {
            return 'Serverfehler!';
        }
        if (error.status === 0) {
            return 'Keine Antwort!';
        }
    }

    return 'Unbekannter Fehler!';
}
